import React, { useCallback, useMemo } from 'react';

import { IconButton } from '@wix/wix-ui-tpa';
import {
  usePanorama,
  useEnvironment,
  useTranslation,
} from '@wix/yoshi-flow-editor';

import {
  minimizeWidget,
  oneChatSettingsModel,
  selectOneChatSettings,
  sendWhatsAppSocialButtonMessage,
} from '@/features';
import { useAppDispatch, useAppSelector } from '@/store';
import Minus from '@/assets/icons/customMinimizeButton.svg';
import Whatsapp from '@/assets/icons/whatsapp-header-icon.svg';

import { MinimizedButtonIcon } from 'AiAssistantWidget/Settings/components/DesignMinimizedButton/minimizedButtonIcons';
import settingsParams from 'AiAssistantWidget/settingsParams';
import { useWidgetSettings } from 'AiAssistantWidget/Widget/contexts';

import { AIAvatarIcon } from '../svg';

import { st, classes } from './Header.st.css';

export const Header = ({ dialogId }: { dialogId?: string }) => {
  const { t } = useTranslation();
  const { isMobile, isEditor } = useEnvironment();
  const settings = useWidgetSettings();
  const dispatch = useAppDispatch();
  const panorama = usePanorama();
  const isAi = useAppSelector(selectOneChatSettings.isAi);
  const socialButtonsWhatsApp = useAppSelector(
    selectOneChatSettings.socialButtonsWhatsApp,
  );
  const socialButtonsWhatsAppEnabled = useAppSelector(
    selectOneChatSettings.socialButtonsWhatsAppEnabled,
  );

  const useAvatar = useMemo(
    () => settings.get(settingsParams.displayChatbotAvatar, 'primary'),
    [settings],
  );

  const name = useMemo(
    () =>
      settings.get(
        isAi ? settingsParams.textHeader : settingsParams.manualChatTextHeader,
      ),
    [isAi, settings],
  );

  const icon = useMemo(() => {
    const iconKey = settings.get(settingsParams.minimizedButtonIcon, 'primary');
    const customIconSrc = settings.get(
      settingsParams.minimizedButtonCustomIcon,
      'primary',
    );

    return iconKey ? (
      <MinimizedButtonIcon
        icon={iconKey}
        customIconSrc={customIconSrc}
        className={st(classes.customIcon)}
      />
    ) : (
      <AIAvatarIcon />
    );
  }, [settings]);

  const onClickMinimize = useCallback(() => {
    dispatch(minimizeWidget());
  }, [dispatch]);

  const onClickWhatsApp = useCallback(() => {
    if (isEditor || !socialButtonsWhatsApp?.phone) {
      return;
    }
    panorama.logger().info('whatsapp-header-icon-clicked', {});
    // Explicitly caught (not just discarded) so the history-load-timeout
    // rejection doesn't look unhandled — reported via Panorama error
    // monitoring inside the thunk itself. No visible retry UI: consistent
    // with every other action-message failure in this codebase (add-to-cart,
    // book-service, etc.), none of which have a toast/notification wired up.
    dispatch(
      sendWhatsAppSocialButtonMessage({
        infoText: socialButtonsWhatsApp.infoText ?? undefined,
        url: oneChatSettingsModel.buildWhatsAppUrl({
          phone: socialButtonsWhatsApp.phone,
          prefilledMessage: socialButtonsWhatsApp.prefilledMessage ?? undefined,
        }),
      }),
    )
      .unwrap()
      .catch(() => {});
  }, [dispatch, isEditor, socialButtonsWhatsApp, panorama]);

  return (
    <div className={st(classes.root, { isMobile })}>
      <div className={st(classes.left)}>
        {useAvatar && <div className={st(classes.avatar)}>{icon}</div>}

        <h2 id={dialogId} className={st(classes.name)} title={name}>
          {name}
        </h2>
      </div>
      <div className={st(classes.right)}>
        {socialButtonsWhatsAppEnabled && (
          <IconButton
            contentClassName={st(classes.whatsAppButton)}
            icon={<Whatsapp />}
            onClick={onClickWhatsApp}
            className={st(classes.whatsAppButton)}
            aria-label={t('app.widget.socialButtons.whatsapp.openButton')}
          />
        )}
        <IconButton
          contentClassName={st(classes.minimizeButton)}
          icon={<Minus />}
          onClick={onClickMinimize}
          className={st(classes.minimizeButton)}
          aria-label={t('app.widget.ariallabel.minimize')}
        />
      </div>
    </div>
  );
};
